import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MiniFAQ from '../MiniPDP/MiniFAQ/MiniFAQ';
import FAQ from '../../views/ProductDetails/Series3CollarDetails/components/FAQ';
import HardwareFirstAiBehaviors from './HardwareFirstAiBehaviors';
import HardwareFirstAppCarousel from './HardwareFirstAppCarousel';
import HardwareFirstVideoModule from './HardwareFirstVideoModule';
import { Species } from '../../types/gql-op-types';
import * as types from '../../types';
import HardwareFirstGpsBand from './HardwareFirstGpsBand';
import { SoloCompareTable, SoloPressReviews } from './HardwareFirstSoloSections';
import {
  HardwareFirstAllFeatures,
  HardwareFirstOwnersSay,
  HardwareFirstQuizBand,
  HardwareFirstSpecs,
} from './HardwareFirstSections';
import styles from './HardwareFirstPickYourFi.module.scss';

export type PickYourFiDeviceKey = 'mini' | 's3' | 'ultra';

/** Each device's kit, one white card per item, matching the figma's single-row box layout. */
export const BOX_CONTENTS: Record<PickYourFiDeviceKey, { url: string; labelKey: string }[]> = {
  mini: [
    { url: '/product_images/mini/box/device.png', labelKey: 'hardware_first_box_mini_tracker' },
    { url: '/product_images/mini/box/cases.png', labelKey: 'hardware_first_box_silicone_cases' },
    { url: '/product_images/mini/box/cable.png', labelKey: 'hardware_first_box_charging_cord' },
  ],
  s3: [
    { url: '/product_images/smart-collar-v3b/gray.png', labelKey: 'hardware_first_box_s3_tracker' },
    { url: '/product_images/additional-band-s3/gray.png', labelKey: 'hardware_first_box_s3_collar' },
    { url: '/product_images/additional-base-s3.png', labelKey: 'hardware_first_box_s3_base' },
  ],
  ultra: [
    { url: '/product_images/ultra/box/device.png', labelKey: 'hardware_first_box_ultra_device' },
    { url: '/product_images/ultra/box/cable.png', labelKey: 'hardware_first_box_ultra_cable' },
  ],
};

export function WhatsInTheBox({ deviceKey }: { deviceKey: PickYourFiDeviceKey }) {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className={styles.boxHeading}>{t('cart_item_v2.whats_included')}</h2>
      <div className={styles.boxGrid}>
        {BOX_CONTENTS[deviceKey].map((item) => (
          <div key={item.url} className={styles.boxCard}>
            <img className={styles.boxImage} src={item.url} alt={t(item.labelKey)} />
            <div className={styles.boxLabel}>{t(item.labelKey)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * The pick-your-fi page's per-device detail sections, swapped by the selected card. Box, specs,
 * all-features, owners-say and the app carousel are scoped to this page per the figma; the FAQ
 * reuses the sections the standalone PDPs maintain so its content stays canonical. The behavior
 * band is dog-only - it reads barking, licking and eating - so ultra and cats skip it.
 */
export default function HardwareFirstBelowFold({
  deviceKey,
  species = Species.DOG,
  showQuiz = true,
}: {
  deviceKey: PickYourFiDeviceKey;
  species?: Species;
  /** False when the lineup hides a tracker the quiz could recommend. */
  showQuiz?: boolean;
}) {
  // the quiz band compares in place; its button toggles the table under it
  const [compareOpen, setCompareOpen] = useState(false);
  return (
    <div className={`${styles.belowFold} ${styles.belowFoldSolo}`}>
      <HardwareFirstGpsBand deviceKey={deviceKey} species={species} />
      {deviceKey !== 'ultra' && species !== Species.CAT && <HardwareFirstAiBehaviors />}
      <WhatsInTheBox deviceKey={deviceKey} />
      <HardwareFirstSpecs deviceKey={deviceKey} species={species} />
      <HardwareFirstAppCarousel deviceKey={deviceKey} species={species} />
      {/* cats have no footage of their own, so the module drops rather than showing a dog */}
      {species !== Species.CAT && <HardwareFirstVideoModule deviceKey={deviceKey} />}
      <HardwareFirstAllFeatures deviceKey={deviceKey} species={species} />
      <HardwareFirstOwnersSay deviceKey={deviceKey} species={species} />
      {/* the quiz band sits beside the comparison; cats have no table */}
      {species !== Species.CAT ? (
        <>
          <HardwareFirstQuizBand onCompare={() => setCompareOpen((open) => !open)} />
          {compareOpen && <SoloCompareTable />}
        </>
      ) : (
        showQuiz && <HardwareFirstQuizBand />
      )}
      {/* the shared faq modules key their compact plp treatment off this global class */}
      <div className="hardware-first-faq">
        {deviceKey === 'mini' && <MiniFAQ species={species} />}
        {deviceKey === 's3' && <FAQ collarId={types.series3PlusCollarId} freeTrialEnabled={false} />}
        {deviceKey === 'ultra' && <FAQ collarId={types.ultraCollarId} freeTrialEnabled={false} />}
      </div>
      {/* the press quotes close the s3+ page, under the faq */}
      {deviceKey === 's3' && <SoloPressReviews />}
    </div>
  );
}
